
import { useState, useCallback, useMemo } from "react";
import { Investor } from "@/types/investor";
import { hookDebugUtils } from "@/utils/ai/debugUtils";

export const useInvestorFiltering = (
  investors: Investor[],
  initialSortField: "relevanceScore" | "firm" = "relevanceScore",
  initialSortDirection: "asc" | "desc" = "desc",
  isInvestorProcessed: (investor: Investor) => boolean
) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState<"relevanceScore" | "firm">(initialSortField);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">(initialSortDirection);

  // Safe render helper to prevent rendering objects directly
  const safeRender = useCallback((value: any): string => {
    if (value === null || value === undefined) return "";
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return value.toString();
    if (typeof value === 'boolean') return value.toString();
    return hookDebugUtils.safeObjectDisplay(value) || "";
  }, []);

  const toggleSortDirection = useCallback(() => {
    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
  }, [sortDirection]);

  const handleSortChange = useCallback((field: "relevanceScore" | "firm") => {
    if (sortField === field) {
      toggleSortDirection();
    } else {
      setSortField(field);
      setSortDirection(field === "relevanceScore" ? "desc" : "asc");
    }
  }, [sortField, toggleSortDirection]);

  const sortedInvestors = useMemo(() => {
    return [...investors].sort((a, b) => {
      // Modified sorting to handle unprocessed investors correctly
      if (sortField === "relevanceScore") {
        // Check if both investors are processed
        const aProcessed = isInvestorProcessed(a);
        const bProcessed = isInvestorProcessed(b);
        
        // If both are processed, sort by relevance score
        if (aProcessed && bProcessed) {
          return sortDirection === "asc"
            ? a.relevanceScore - b.relevanceScore
            : b.relevanceScore - a.relevanceScore;
        }
        
        // If only one is processed, prioritize processed investors
        if (aProcessed && !bProcessed) {
          return sortDirection === "asc" ? 1 : -1; // Processed last in asc, first in desc
        }
        
        if (!aProcessed && bProcessed) {
          return sortDirection === "asc" ? -1 : 1; // Unprocessed first in asc, last in desc
        }
        
        // If neither is processed, fall back to firm name
        const firmA = a.firm.toLowerCase();
        const firmB = b.firm.toLowerCase();
        return sortDirection === "asc"
          ? firmA.localeCompare(firmB)
          : firmB.localeCompare(firmA);
      } else {
        // For firm sorting, use standard alphabetical comparison
        const firmA = a.firm.toLowerCase();
        const firmB = b.firm.toLowerCase();
        return sortDirection === "asc"
          ? firmA.localeCompare(firmB)
          : firmB.localeCompare(firmA);
      }
    });
  }, [investors, sortField, sortDirection, isInvestorProcessed]);

  const filteredInvestors = useMemo(() => {
    return sortedInvestors.filter(
      (investor) =>
        safeRender(investor.firm).toLowerCase().includes(searchTerm.toLowerCase()) ||
        (investor.name && safeRender(investor.name).toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [sortedInvestors, searchTerm, safeRender]);

  return {
    searchTerm,
    setSearchTerm,
    sortField,
    sortDirection,
    handleSortChange,
    filteredInvestors,
    processedCount: investors.filter(isInvestorProcessed).length,
    totalCount: investors.length
  };
};
